import { Injectable, OnDestroy, Signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { forkJoin, of, Subscription } from 'rxjs';
import enTranslations from '../assets/i18n/en-EN.json';
import itTranslations from '../assets/i18n/it-IT.json';
import jaTranslations from '../assets/i18n/ja-JP.json';
import { Apollo } from 'apollo-angular';
import { LanguageSettingsQuery, RegisterLoginMutation } from 'gql/graphql';
import { CognitoService, UserWithID } from '@services/cognito/cognito.service';
import { languageSettingsQuery, uiSettingsQuery } from '@graphql/general';
import { mapIdToLang, mapLangToFileName } from '@constants/settings';
import { UserSettingsService } from '@services/user/user-settings.service';
import { ThemeService } from '@services/ui/theme.service';
import { getErrorMessage } from '@utils/errors';
import { ToastService } from '@services/ui/toast.service';
import { Router } from '@angular/router';
import { URLS } from '@constants/urls';
import { OverlayService } from '@services/ui/overlay.service';
import { registerLogin } from '@graphql/user';

function normalizeUrl(url: string): string {
  if (!url.startsWith('/')) {
    url = '/' + url;
  }
  if (url !== '/' && url.endsWith('/')) {
    url = url.slice(0, -1);
  }
  return url;
}

@Injectable({
  providedIn: 'root',
})
export class AppInitializer implements OnDestroy {
  private user: Signal<UserWithID | null>;
  private defaultLang = 'en-EN';

  private subscriptions: Subscription[] = [];
  constructor(
    private readonly translateService: TranslateService,
    private readonly apollo: Apollo,
    private readonly cognitoService: CognitoService,
    private readonly uiSettings: UserSettingsService,
    private readonly overlay: OverlayService,
    readonly router: Router
  ) {
    this.user = cognitoService.loggedUser;
    this.cognitoService.getAuthenticationSubject().subscribe(() => {
      if (!cognitoService.isAuthenticated()) {
        const publicRoutes = [
          `${URLS.BASE}${URLS.LOGIN}`,
          `${URLS.BASE}${URLS.SIGNUP}`,
        ];
        const currentUrl = normalizeUrl(window.location.pathname);
        if (!publicRoutes.includes(currentUrl)) {
          overlay.activeModalOverlay?.dispose();
          overlay.activeSidePanelOverlay?.dispose();
          this.router.navigate([URLS.BASE, URLS.LOGIN]);
        }
      }
    });
  }

  initialize() {
    return new Promise<void>(async (resolve, reject) => {
      try {
        await this.cognitoService.getToken();
        if (this.cognitoService.isLoggedIn()) {
          await this.checkSettingsLanguage();
          this.uiSettings.setUserAccount(null); // Reset del persistentSignal
          await this.uiSettings.getUserAccount();
          await this.uiSettings.checkUIUserSettings();
          await this.uiSettings.getUserAvatar();
          this.apollo
            .mutate<RegisterLoginMutation>({
              mutation: registerLogin,
            })
            .subscribe({
              next: () => {},
              error: (err) => {},
            });
        } else {
          const browserLang = navigator.language;
          const appLang = mapLangToFileName[browserLang] || this.defaultLang;
          this.cognitoService.setAppLanguage(appLang);
          await this.setLanguage(appLang);
        }
        resolve();
      } catch (error) {
        console.error('Error during app initialization:', error);
        reject(error);
      }
    });
  }

  private async checkSettingsLanguage() {
    const lang = this.cognitoService.language();
    if (!!lang) {
      await this.setLanguage(lang);
    } else {
      this.subscriptions.push(
        this.apollo
          .watchQuery<LanguageSettingsQuery>({
            query: languageSettingsQuery,
          })
          .valueChanges.subscribe(async ({ data }) => {
            const settingsLanguage =
              mapLangToFileName[mapIdToLang[data.languageSettings.id]] ||
              'en-EN';
            this.cognitoService.setAppLanguage(settingsLanguage);
            await this.setLanguage(settingsLanguage);
          })
      );
    }
  }

  private setLanguage(language: string) {
    return new Promise<void>((resolve, reject) => {
      this.translateService.setDefaultLang(language);
      const enFile = of(enTranslations);
      const itFile = of(itTranslations);
      const jaFile = of(jaTranslations);

      forkJoin([itFile, enFile, jaFile]).subscribe(() => {
        this.translateService.use(language).subscribe({
          next: () => {
            if (language === 'ja-JP' || language.startsWith('ja')) {
              document.body.classList.add('jp-font');
            } else {
              document.body.classList.remove('jp-font');
            }
            console.log(
              `Successfully initialized translations with language set to: ${language}.`
            );
            resolve();
          },
          error: (err) => {
            console.error('Error initializing translations:', err);
            reject();
          },
        });
      });
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }
}
